import { useEffect, useState } from "react";
import { PALETTE as P } from "../tokens";
import { useAuth } from "../context/AuthContext";
import {
  enablePush,
  isPushCapable,
} from "../services/pushNotifications";
import { isNativeIOS } from "../utils/platform";
import PushPrePrompt from "./PushPrePrompt";

// Non-intrusive Web Push opt-in prompt for the Dashboard. Visible only
// after the user has built some signal (≥ 3 check-ins) and only on a
// browser that can actually deliver notifications — earning the right
// to ask before we ask.
//
// Lifecycle:
//   • default       — soft card, gold outlined CTA + "Not now"
//   • requesting    — CTA disabled, "Enabling…" pulse copy
//   • granted       — card hides, 3-second toast confirms
//   • denied        — card hides silently, never asks again on this
//                     device (localStorage flag set)
//
// Gate rules live in ``shouldShow`` — easier to reason about than
// inlining in JSX, and they're shared with the Dashboard wire-up which
// also checks them before mounting at all.

const STORAGE_KEY = "haiven_push_prompt_dismissed";
const CHECKINS_GATE = 3;
const TOAST_MS = 3000;


export function shouldShow({ totalCheckins }) {
  if (typeof window === "undefined") return false;
  if (!isPushCapable()) return false;
  // Web: never re-ask once the browser has a verdict. Native iOS has no
  // Notification global — permission state is checked inside the flow
  // (requestPermissions returns the settled verdict without re-prompting),
  // and the localStorage dismissal below covers "asked and declined".
  if (!isNativeIOS() && typeof Notification !== "undefined" &&
      Notification?.permission !== "default") return false;
  if ((totalCheckins ?? 0) < CHECKINS_GATE) return false;
  try {
    if (localStorage.getItem(STORAGE_KEY) === "1") return false;
  } catch { /* private mode */ }
  return true;
}


function _dismiss() {
  try { localStorage.setItem(STORAGE_KEY, "1") } catch { /* private mode */ }
}


export default function PushNotificationPrompt({ totalCheckins }) {
  const { token } = useAuth();
  // ``hidden`` is the local view-state — separate from the persistent
  // localStorage dismissal so a granted/denied result hides the card
  // immediately without waiting on the next mount.
  const [hidden, setHidden] = useState(() => !shouldShow({ totalCheckins }));
  const [status, setStatus] = useState("default"); // default | requesting | granted | denied | install-required

  // totalCheckins arrives async (Dashboard's snapshot fetch) — the
  // initializer above ran with 0 and locked the card hidden. Re-open
  // when the gate later clears, but never resurface past an explicit
  // outcome (granted/denied set status; dismissals set localStorage,
  // which shouldShow re-checks).
  useEffect(() => {
    if (hidden && status === "default" && shouldShow({ totalCheckins })) {
      setHidden(false);
    }
  }, [totalCheckins, hidden, status]);
  const [toastVisible, setToastVisible] = useState(false);

  // Auto-hide the granted toast after TOAST_MS.
  useEffect(() => {
    if (!toastVisible) return;
    const id = setTimeout(() => setToastVisible(false), TOAST_MS);
    return () => clearTimeout(id);
  }, [toastVisible]);

  if (hidden) {
    // Toast can outlive the card itself when the user grants.
    return toastVisible ? <GrantedToast /> : null;
  }

  // Tapping the card CTA no longer fires the native permission dialog —
  // it opens the pre-prompt explainer (the WHY screen). Only the
  // explainer's own CTA spends the one-shot native ask.
  const [prePromptOpen, setPrePromptOpen] = useState(false);

  const onPrePromptClose = (result) => {
    setPrePromptOpen(false);
    if (result === "subscribed") {
      setStatus("granted");
      setToastVisible(true);
      setHidden(true);
      // Granted users don't dismiss the card via localStorage — if
      // they uninstall and reinstall the PWA the prompt should
      // resurface to re-subscribe. Permission state itself is the
      // real source of truth.
      return;
    }
    if (result === "denied") {
      setStatus("denied");
      _dismiss();
      setHidden(true);
      return;
    }
    if (result === "dismissed") {
      // "Not now" on the explainer — soft dismiss, same as the card's.
      _dismiss();
      setHidden(true);
      return;
    }
    if (result === "install-required") {
      // Deliberately NOT a dismissal — no localStorage flag, no hide.
      // Notification.permission is still 'default' (we bail before
      // ever asking), so shouldShow() keeps this card eligible; the
      // card just swaps to instructional copy until the user either
      // installs and comes back, or taps away.
      setStatus("install-required");
      return;
    }
    // 'error' — fall back to the default state with no copy change. The
    // user can try again or use the Profile toggle later.
    setStatus("default");
  };

  const onNotNow = () => {
    _dismiss();
    setHidden(true);
  };

  return (
    <div
      className="fu"
      style={{
        background: P.surface,
        border: `0.5px solid ${P.border}`,
        borderRadius: 14,
        padding: "16px 18px",
        marginBottom: 16,
      }}
    >
      {prePromptOpen && (
        <PushPrePrompt
          onEnable={() => enablePush(token)}
          onClose={onPrePromptClose}
        />
      )}
      <p style={{
        fontSize: 10, fontWeight: 500, color: P.primary,
        letterSpacing: "0.08em", margin: "0 0 6px",
      }}>
        GET DAILY REMINDERS
      </p>
      {status === "install-required" ? (
        <>
          <p style={{
            fontSize: 14, color: P.text, lineHeight: 1.5,
            margin: "0 0 14px",
          }}>
            iOS only delivers reminders once Haiven is added to your Home
            Screen. Tap the Share icon in Safari, choose "Add to Home
            Screen," then open Haiven from there and try again.
          </p>
          <button
            type="button"
            onClick={() => setStatus("default")}
            style={{
              background: "transparent",
              color: P.primary,
              border: `1px solid ${P.primary}`,
              borderRadius: 10,
              padding: "10px 14px",
              fontSize: 13,
              fontWeight: 500,
              fontFamily: "'DM Sans', sans-serif",
              cursor: "pointer",
              minHeight: 44,
            }}
          >
            Got it
          </button>
        </>
      ) : (
        <>
          <p style={{
            fontSize: 14, color: P.text, lineHeight: 1.5,
            margin: "0 0 14px",
          }}>
            Never miss a check-in. We'll nudge you at the right time.
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button
              type="button"
              onClick={() => setPrePromptOpen(true)}
              disabled={status === "requesting"}
              aria-busy={status === "requesting" ? "true" : "false"}
              style={{
                background: "transparent",
                color: P.primary,
                border: `1px solid ${P.primary}`,
                borderRadius: 10,
                padding: "10px 14px",
                fontSize: 13,
                fontWeight: 500,
                fontFamily: "'DM Sans', sans-serif",
                cursor: status === "requesting" ? "default" : "pointer",
                minHeight: 44,
                animation: status === "requesting"
                  ? "haiven-dd-pulse 1.6s ease-in-out infinite"
                  : "none",
              }}
            >
              {status === "requesting" ? "Enabling…" : "Turn on reminders"}
            </button>
            <button
              type="button"
              onClick={onNotNow}
              style={{
                background: "none", border: "none",
                color: P.textMute, fontSize: 12.5,
                cursor: "pointer", padding: "10px 4px",
                fontFamily: "'DM Sans', sans-serif",
                minHeight: 44,
              }}
            >
              Not now
            </button>
          </div>
        </>
      )}
    </div>
  );
}


function GrantedToast() {
  return (
    <div
      role="status"
      style={{
        position: "fixed",
        bottom: 100,
        left: "50%",
        transform: "translateX(-50%)",
        background: P.dark,
        color: P.bg,
        borderRadius: 999,
        padding: "10px 18px",
        fontSize: 13,
        fontFamily: "'DM Sans', sans-serif",
        boxShadow: "0 4px 20px rgba(28,24,20,0.2)",
        zIndex: 50,
      }}
    >
      Reminders on. We'll see you tomorrow.
    </div>
  );
}
